import {Injectable} from "@angular/core";
import {ISelection, IQuestionEntry} from "../../api/data-model";
import {QuestionState, CompletionState, WordWallMode} from "../usermodel/usermodel.objects";
import {
    StepInitializedEvent, PreviewStepEvent, StepStatusEvent, ReadAndRecordStepEvent,
    QuizStepEvent, StepAllowedEvent
} from "../../event/StepEvents";
import {BookIntroLaunchEvent, BookIntroEndEvent} from "../../event/ContentEvents";
import {StepService} from "./step.service";
import {EventBus} from "../../event/EventBus";
import {WordWallLaunchEvent} from "../../event/WordWallEvents";
import {ClientConfiguration} from "../configuration/client.config";
import {UserModelService} from "../usermodel/usermodel.service";
import {BookSessionType, Scilearn} from "../../constants";

export enum StepStatus {
    PROFICIENCY = <any>"PROFICIENCY",
    COMPLETION = <any>"COMPLETION",
    PREVIOUSLY_PROFICIENCY = <any>"PREVIOUSLY_PROFICIENCY",
    PREVIOUSLY_COMPLETION = <any>"PREVIOUSLY_COMPLETION",
    UNDONE = <any>"UNDONE"
}

@Injectable()
export class StepStateUpdater {

    private stepOneStatus: StepStatus;
    private stepTwoStatus: StepStatus;
    private stepThreeStatus: StepStatus;

    private stepOneCompletionState: CompletionState;
    private stepTwoCompletionState: CompletionState;
    private stepThreeCompletionState: CompletionState;
    private RECORD_COUNT_COMPLETE: number = 3;

    constructor(private userModelService: UserModelService,
                private stepService: StepService,
                private eventBus:EventBus) {}

    public determineCurrentStep(selection: ISelection, isInitializeState: boolean):void {

        let stepOneDone:  boolean = this.handleIfStepOneDone(selection);
        let stepTwoDone:  boolean = this.handleIfStepTwoDone(selection);
        let stepThreeDone:  boolean = this.handleIfStepThreeDone(selection);

        if (isInitializeState) {
            this.resetStepStatusesAndCompletionState();
        } else {
            this.setupStepCompletedStates();
        }

        this.triggerProgressAndStepEvents(selection);
        // I am not sure why we are not using the usermodel states rather than our own step-states here
        // can this be simplified?
        this.updateStepOneState(stepOneDone, isInitializeState);
        this.updateStepTwoState(stepTwoDone, isInitializeState);
        this.updateStepThreeState(stepThreeDone, isInitializeState);

        if (isInitializeState) {
            this.stepService.requestStep(this.stepService.getStepFromType(this.userModelService.currentStep()));
            this.eventBus.next(new StepInitializedEvent());
        }
    }

    handleIfStepOneDone(selection: ISelection): boolean {
        // is step-one done
        let taqEntries = selection.questionEntries.filter(entry => entry.presentation === Scilearn.THINK_ABOUT_IT); // use QuestionService?
        let taqStateValues = (this.userModelService.thinkAboutQuestionStates)
            ? Object.keys(this.userModelService.thinkAboutQuestionStates)
                                  .map(k => this.userModelService.thinkAboutQuestionStates[k].status) : [];
        let stepOneDone = taqStateValues.length === taqEntries.length &&
                            taqStateValues.indexOf(QuestionState.ANSWERED_INCORRECTLY) === -1;
        if (stepOneDone) {
            this.stepService.makeStepAvailable(BookSessionType.FLUENCY);
            this.eventBus.next(new StepAllowedEvent(ReadAndRecordStepEvent.TYPE));
        }
        return stepOneDone;
    }

    handleIfStepTwoDone(selection: ISelection): boolean {
        // is step-two done
        let stepTwoDone = this.userModelService.recordCount > 2 || (this.userModelService.recordCount > 1
                                                                      && this.userModelService.hasRecordingStar);
        if (stepTwoDone) {
            this.stepService.makeStepAvailable(BookSessionType.QUIZ);
            this.eventBus.next(new StepAllowedEvent(QuizStepEvent.TYPE));
        }
        return stepTwoDone;
    }

    handleIfStepThreeDone(selection: ISelection): boolean {
        // is step-three done
        let quizEntries = selection.questionEntries.filter(entry => entry.presentation === Scilearn.QUIZ_QUESTION);
        let quizStateValues = (this.userModelService.quizQuestionStates)
            ? Object.keys(this.userModelService.quizQuestionStates)
                .map(k => this.userModelService.quizQuestionStates[k].status) : [];
        let stepThreeDone = quizStateValues.length === quizEntries.length &&
                            quizStateValues.indexOf(QuestionState.ANSWERED_INCORRECTLY) === -1;
        return stepThreeDone;
    }

    public getNumAnsweredCorrectly(questionStates: Object[]): number {
        let answerStateValues = (questionStates)
            ? Object.keys(questionStates)
                                  .map(k => questionStates[k].status) : [];
        return answerStateValues.filter((state) => state === QuestionState.ANSWERED_CORRECTLY).length;
    }

    // ----------------------------
    // book-intro -> wordwall -> steps (1, 2, 3)
    public determineCurrentActivity(selection: ISelection): void {

        // if they haven't finished wordwall you start all over
        if (this.userModelService.currentStep() === BookSessionType.WORDWALL) {
            this.eventBus.next(new BookIntroLaunchEvent());
        } else {
            this.determineCurrentStep(selection, true);
        }
    }

    public determineCurrentActivityAfterBookIntro(): void {
        let isGuided = WordWallMode.GUIDED === this.userModelService.wordWallMode;
        this.eventBus.next(new WordWallLaunchEvent(isGuided));
    }

    private triggerProgressAndStepEvents(selection: ISelection): void {
        let taqEntries = selection.questionEntries.filter(entry => entry.presentation === Scilearn.THINK_ABOUT_IT); // use QuestionService?
        let quizEntries = selection.questionEntries.filter(entry => entry.presentation === Scilearn.QUIZ_QUESTION);

        this.eventBus.next(new StepStatusEvent(PreviewStepEvent.TYPE,
            this.calculateProgress(taqEntries.length,
                                   this.getNumAnsweredCorrectly(this.userModelService.thinkAboutQuestionStates))));

        this.eventBus.next(new StepStatusEvent(ReadAndRecordStepEvent.TYPE,
            this.calculateProgress(this.RECORD_COUNT_COMPLETE, this.recordCountProgress())
        ));

        this.eventBus.next(new StepStatusEvent(QuizStepEvent.TYPE,
            this.calculateProgress(quizEntries.length,
                                   this.getNumAnsweredCorrectly(this.userModelService.quizQuestionStates))));

        this.eventBus.next(new StepAllowedEvent(PreviewStepEvent.TYPE));
        this.stepService.makeStepAvailable(BookSessionType.PREVIEW);
    }

    // after 2 recordings if user has a recording star the user will be bumped
    // up to a 3 recordings, which is the number we need to complete the step
    public recordCountProgress(): number {
        return this.userModelService.recordCount +
            ((this.userModelService.recordCount === 2 && this.userModelService.hasRecordingStar) ? 1 : 0);
    }

    public get currentStepOneStatus(): StepStatus {
        return this.stepOneStatus;
    }

    public get currentStepTwoStatus(): StepStatus {
        return this.stepTwoStatus;
    }

    public get currentStepThreeStatus(): StepStatus {
        return this.stepThreeStatus;
    }

    private calculateProgress(total:number, value:number):number {
        return value === 0 ? 0 : (value / total) * 100;
    }

    private getPartCount(stepOneDone: boolean, stepTwoDone: boolean, stepThreeDone: boolean): number {
        let partCount = 0;
        if (stepOneDone) {
            partCount++;
        }
        if (stepTwoDone) {
            partCount++;
        }
        if (stepThreeDone) {
            partCount++;
        }
        return partCount;
    }

    private stepPreviouslyFinished(starAwarded: boolean, stepStatus: StepStatus): boolean {
        // the first two cases handle the possibility the user retried the step and
        // got full points whereas the first time they have had only a completion status
        // whereas this new attempt gives them a full proficient state
        if (starAwarded && stepStatus === StepStatus.PREVIOUSLY_PROFICIENCY) {
            return true;
        } else if (starAwarded && stepStatus !== StepStatus.PREVIOUSLY_PROFICIENCY) {
            return false;
        } else {
            return stepStatus === StepStatus.PREVIOUSLY_COMPLETION;
        }
    }

    private updateStepOneState(stepOneDone: boolean, initialize: boolean): void {
        if (stepOneDone) {
            if (initialize || this.stepPreviouslyFinished(this.userModelService.hasTAQStar, this.stepOneStatus)) {
                this.stepOneStatus = this.userModelService.hasTAQStar ?
                    StepStatus.PREVIOUSLY_PROFICIENCY : StepStatus.PREVIOUSLY_COMPLETION;
            } else {
                this.stepOneStatus = this.userModelService.hasTAQStar
                    ? StepStatus.PROFICIENCY : StepStatus.COMPLETION;
            }
        } else {
            this.stepOneStatus = StepStatus.UNDONE;
        }
    }

    private updateStepTwoState(stepTwoDone: boolean, initialize: boolean): void {
        if (stepTwoDone) {
            this.stepService.makeStepAvailable(BookSessionType.QUIZ);
            this.eventBus.next(new StepAllowedEvent(QuizStepEvent.TYPE));

            if (initialize ||
                this.stepPreviouslyFinished(this.userModelService.hasRecordingStar, this.stepTwoStatus)) {
                this.stepTwoStatus =
                    this.userModelService.hasRecordingStar ?
                    StepStatus.PREVIOUSLY_PROFICIENCY : StepStatus.PREVIOUSLY_COMPLETION;
            } else {
                this.stepTwoStatus = this.userModelService.hasRecordingStar ?
                                     StepStatus.PROFICIENCY : StepStatus.COMPLETION;
            }
        } else {
            this.stepTwoStatus = StepStatus.UNDONE;
        }
    }

    private updateStepThreeState(stepThreeDone: boolean, initialize: boolean): void {
        if (stepThreeDone) {
            if (initialize || this.stepPreviouslyFinished(this.userModelService.hasQuizStar, this.stepThreeStatus)) {
                this.stepThreeStatus = this.userModelService.hasQuizStar ?
                                       StepStatus.PREVIOUSLY_PROFICIENCY : StepStatus.PREVIOUSLY_COMPLETION;
            } else if (this.quizCompletionState === CompletionState.COMPLETED) {
                this.stepThreeStatus = this.userModelService.hasQuizStar ?
                                       StepStatus.PROFICIENCY : StepStatus.COMPLETION;
            }
        } else {
            this.stepThreeStatus = StepStatus.UNDONE;
        }
    }

    private resetStepStatusesAndCompletionState(): void {
        this.stepOneStatus = StepStatus.UNDONE;
        this.stepTwoStatus = StepStatus.UNDONE;
        this.stepThreeStatus = StepStatus.UNDONE;

        this.stepOneCompletionState = this.userModelService.taqCompletionState === CompletionState.COMPLETED ?
            CompletionState.COMPLETED : CompletionState.NOT_STARTED;
        this.stepTwoCompletionState = this.userModelService.recordCompletionState === CompletionState.COMPLETED ?
            CompletionState.COMPLETED : CompletionState.NOT_STARTED;
        this.stepThreeCompletionState = this.userModelService.quizCompletionState === CompletionState.COMPLETED ?
            CompletionState.COMPLETED : CompletionState.NOT_STARTED;
    }

    private setupStepCompletedStates(): void {
        this.stepOneCompletionState = this.userModelService.taqCompletionState;
        this.stepTwoCompletionState = this.userModelService.recordCompletionState;
        this.stepThreeCompletionState = this.userModelService.quizCompletionState;
  }

    public get taqCompletionState(): CompletionState {
        return this.stepOneCompletionState;
    }

    public get quizCompletionState(): CompletionState {
        return this.stepThreeCompletionState;
    }

    public get recordCompletionState(): CompletionState {
        return this.stepTwoCompletionState;
    }

    public isStepNotCompleted(completionState: CompletionState): boolean {
        return completionState !== CompletionState.COMPLETED;
    }

    public isStepCompleted(completionState: CompletionState): boolean {
        return completionState === CompletionState.COMPLETED;
    }
}
